import * as React from 'react'
import { useSnackbar } from 'notistack'
import { useRouter } from 'next/router'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import AvatarGroup from '@mui/material/AvatarGroup'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import { useUser } from 'components/hooks/state'
import { I18n, useTranslation } from 'components/hooks/use-translation'

import Button from 'components/atomic/atoms/button'
import Avatar from 'components/atomic/atoms/avatar'
import Input from 'components/atomic/atoms/input'
import Text from 'components/atomic/atoms/text'
import Card from 'components/atomic/atoms/card'

import devvSdk from 'utils/devv-sdk'
import Routes, { toAbsoluteUrl } from 'utils/routes'
import { PathGoal, Track, Path } from 'types/types'
import analytics, { AnalyticsEvents } from 'utils/analytics'

export const CTABuy = (props: { _id: string; enrolledUsers: number; isPro?: boolean; itemType: PathGoal }) => {
    const [loading, setLoading] = React.useState(false)

    const router = useRouter()
    const { t }: I18n = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { mutate } = useUser()

    const handleEnrollPro = React.useCallback(() => {
        setLoading(true)

        const action = props.itemType === 'course' ? devvSdk.enrollUserInCourse : devvSdk.enrollUserInPath

        action(props._id)
            .then(() => {
                enqueueSnackbar(t('path:success.userEnrolled'), { variant: 'success' })
                mutate()
            })
            .catch(() => {
                // TODO: handle specific errors
                enqueueSnackbar(t('common:errors.generic'), { variant: 'error' })
            })
            .then(() => {
                setLoading(false)
            })
    }, [props._id, props.itemType, enqueueSnackbar, mutate, t])

    const handleBuyNow = async () => {
        // TODO: implement
        enqueueSnackbar('Azione non gestita', { variant: 'warning' })
    }

    const handleBecomePro = () => {
        router.push(Routes.Pricing)
    }

    return (
        <Card variant="elevation" style={{ height: '' }}>
            <Stack spacing={2} p={1}>
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Text variant="h3">{props.isPro ? '0.00$' : '9.99$'}</Text>
                        <Text variant="subtitle1">invece di </Text>
                        <Text
                            variant="h5"
                            color="text.secondary"
                            fontWeight={400}
                            sx={{ textDecoration: 'line-through' }}>
                            19.99$
                        </Text>
                    </Stack>
                    <Text variant="subtitle2">
                        {props.isPro
                            ? 'Solo per i membri PRO come te'
                            : 'Per un periodo limitato, se acquisti il corso singolarmente.'}
                    </Text>
                </Stack>

                <br />

                {props.isPro ? (
                    <Button
                        loading={loading}
                        size="large"
                        onClick={() => handleEnrollPro()}
                        variant="contained"
                        fullWidth>
                        Inizia ora
                    </Button>
                ) : (
                    <Button size="large" onClick={() => handleBuyNow()} variant="contained" fullWidth>
                        Acquista ora
                    </Button>
                )}

                {/** User number */}
                <Grid container alignItems="center">
                    <Text variant="subtitle2">{t('path:hero.join')}</Text>

                    <AvatarGroup spacing={14} sx={{ mx: 1 }}>
                        <Avatar alt="" sx={{ width: 20, height: 20 }} />
                        <Avatar
                            alt=""
                            sx={{ width: 20, height: 20 }}
                            src={`https://i.pravatar.cc/20?u=${props._id}2`}
                        />
                        <Avatar
                            alt=""
                            sx={{ width: 20, height: 20 }}
                            src={`https://i.pravatar.cc/20?u=${props._id}3`}
                        />
                    </AvatarGroup>

                    <Text variant="subtitle2" sx={{ mr: 0.5 }}>
                        {t('path:hero.nStudents', { count: props.enrolledUsers })}
                    </Text>
                    <Text variant="subtitle2">{t('path:hero.alreadyJoined')}</Text>
                </Grid>

                {!props.isPro && (
                    <>
                        <Divider />

                        <Stack spacing={1}>
                            <Text variant="h5">Gratis per i PRO</Text>
                            <Text variant="subtitle2">
                                Diventa parte dei devv pro e ottieni accesso illimitato a tutti i corsi..
                            </Text>
                        </Stack>

                        <Button golden size="large" onClick={() => handleBecomePro()} variant="outlined" fullWidth>
                            PROVA PRO GRATIS
                        </Button>
                    </>
                )}
            </Stack>
        </Card>
    )
}

interface CTALeadProps {
    _id: string
    enrolledUsers: number
    joinAction: 'enroll' | 'pre-enroll'
    item: Track | Path
    itemType: PathGoal
    title: string
    description: string
    buttonText: string
    proAlternative?: boolean
}

export const CTALead = (props: CTALeadProps) => {
    const [loading, setLoading] = React.useState(false)
    const [successMessage, setSuccessMessage] = React.useState('')
    const [isEnrolled, setIsEnrolled] = React.useState(false)

    const { t }: I18n = useTranslation()
    const { user, mutate } = useUser()
    const { enqueueSnackbar } = useSnackbar()
    const router = useRouter()

    const schema = Yup.object().shape({
        email: Yup.string().email(t('common:errors.invalidEmail')).required(t('common:errors.requiredField')),
    })

    React.useEffect(() => {
        if (user) {
            const isEnrolled =
                user?.pre_enrolled_paths?.includes(props._id) || user?.pre_enrolled_tracks?.includes(props._id)
            setIsEnrolled(isEnrolled)
            if (isEnrolled) setSuccessMessage(t('path:success.userPreEnrolled'))
        }
    }, [user, props._id, t])

    const handleEnroll = React.useCallback(
        (email?: string) => {
            const action = props.itemType === 'course' ? devvSdk.enrollUserInCourse : devvSdk.enrollUserInPath

            action(props._id, email)
                .then(() => {
                    if (user) {
                        return t('path:success.userEnrolled')
                    } else {
                        //  TODO: we should show a popup for the user to complete the account
                        return t('path:success.anonimousUserEnrolled')
                    }
                })
                .then((msg) => {
                    analytics.track(AnalyticsEvents.Enroll, {
                        email,
                        item_name: props.item.title,
                        item_link:
                            props.itemType === 'course'
                                ? toAbsoluteUrl(Routes.Course(props.item.slug))
                                : toAbsoluteUrl(Routes.Path(props.item.slug)),
                    })
                    enqueueSnackbar(msg, { variant: 'success' })
                    setSuccessMessage(msg)
                    setIsEnrolled(true)
                    mutate()
                })
                .catch(() => {
                    // TODO: handle specific errors
                    enqueueSnackbar(t('common:errors.generic'), { variant: 'error' })
                })
                .then(() => {
                    setLoading(false)
                })
        },
        [enqueueSnackbar, mutate, props._id, props.item.slug, props.item.title, props.itemType, t, user],
    )

    const handlePreEnroll = React.useCallback(
        (email?: string) => {
            const action = props.itemType === 'course' ? devvSdk.preEnrollUserInCourse : devvSdk.preEnrollUserInPath

            action(props._id, email)
                .then(() => {
                    if (user) {
                        return t('path:success.userPreEnrolled')
                    } else {
                        //  TODO: we should show a popup for the user to complete the account
                        return t('path:success.anonimousUserPreEnrolled')
                    }
                })
                .then((msg) => {
                    analytics.track(AnalyticsEvents.PreEnroll, {
                        email,
                        item_name: props.item.title,
                        item_link:
                            props.itemType === 'course'
                                ? toAbsoluteUrl(Routes.Course(props.item.slug))
                                : toAbsoluteUrl(Routes.Path(props.item.slug)),
                    })

                    enqueueSnackbar(msg, { variant: 'success' })
                    setSuccessMessage(msg)
                    setIsEnrolled(true)
                    mutate()
                })
                .catch(() => {
                    // TODO: handle specific errors
                    enqueueSnackbar(t('common:errors.generic'), { variant: 'error' })
                })
                .then(() => {
                    setLoading(false)
                })
        },
        [enqueueSnackbar, mutate, props._id, props.item.slug, props.item.title, props.itemType, t, user],
    )

    const handleSubmit = async (values: { email?: string }) => {
        setLoading(true)
        if (props.joinAction === 'enroll') handleEnroll(values.email)
        else handlePreEnroll(values.email)
    }

    const handleBecomePro = () => {
        router.push(Routes.Pricing)
    }

    const form = useFormik({
        onSubmit: handleSubmit,
        initialValues: !user ? { email: '' } : {},
        validationSchema: !user ? schema : null,
    })

    return (
        <Card variant="elevation" style={{ height: '' }}>
            <Stack spacing={2} p={1}>
                <Stack spacing={1}>
                    <Text variant="h5">{props.title}</Text>
                    <Text variant="subtitle2">{props.description}</Text>
                </Stack>

                {/* <br /> */}

                {/** Email input */}
                {!user && !isEnrolled && (
                    <Input
                        label={t('path:hero.email')}
                        size="small"
                        onChange={form.handleChange('email')}
                        value={form.values.email}
                        error={form.touched.email ? !!form.errors.email : undefined}
                        helperText={form.touched.email ? form.errors.email : undefined}
                    />
                )}

                {/** Enroll button */}
                {!isEnrolled && (
                    <Button
                        loading={loading}
                        size="large"
                        onClick={() => form.handleSubmit()}
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardRoundedIcon />}>
                        {props.buttonText}
                    </Button>
                )}

                {successMessage && (
                    <Alert variant="outlined" severity="success">
                        {successMessage}
                    </Alert>
                )}

                {/** User number */}
                <Grid container alignItems="center">
                    {!isEnrolled && <Text variant="subtitle2">{t('path:hero.join')}</Text>}

                    <AvatarGroup spacing={14} sx={{ mx: 1 }}>
                        <Avatar alt="" sx={{ width: 20, height: 20 }} />
                        <Avatar
                            alt=""
                            sx={{ width: 20, height: 20 }}
                            src={`https://i.pravatar.cc/20?u=${props._id}2`}
                        />
                        <Avatar
                            alt=""
                            sx={{ width: 20, height: 20 }}
                            src={`https://i.pravatar.cc/20?u=${props._id}3`}
                        />
                    </AvatarGroup>

                    <Text variant="subtitle2" sx={{ mr: 0.5 }}>
                        {t('path:hero.nStudents', { count: props.enrolledUsers })}
                    </Text>
                    <Text variant="subtitle2">
                        {props.joinAction === 'enroll' ? t('path:hero.alreadyJoined') : t('path:hero.inWaitingList')}
                    </Text>
                </Grid>

                {props.proAlternative && (
                    <>
                        <Divider />

                        <Stack spacing={1}>
                            <Text style={{ marginTop: '2rem' }} variant="h5">
                                Vuoi accedere subito?
                            </Text>
                            <Text variant="subtitle2">
                                Questo corso è disponibile subito per i membri PRO e Supporters
                            </Text>
                        </Stack>

                        <Button
                            golden={true}
                            size="large"
                            onClick={() => handleBecomePro()}
                            variant="outlined"
                            fullWidth>
                            Diventa un PRO
                        </Button>
                    </>
                )}
            </Stack>
        </Card>
    )
}
